import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import CakeCollection from './CakeCollection';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Gift, Truck, Heart, Clock, Sparkles, MapPin } from 'lucide-react';

const Divider = () => (
  <div className="flex items-center justify-center gap-3 text-[#6B7C3F]">
    <span className="h-px w-12 bg-[#6B7C3F]/40" />
    <Heart className="w-4 h-4 fill-current" />
    <span className="h-px w-12 bg-[#6B7C3F]/40" />
  </div>
);

const ModernNenaMiami = ({ onCartOpen }) => {
  const { dispatch } = useCart();
  const [featuredCake, setFeaturedCake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const fetchFeaturedCake = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
        const data = await response.json();
        const orderOneProduct = data.find(cake => cake.displayOrder === 1);
        setFeaturedCake(orderOneProduct || data[0]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching featured cake:', error);
        setLoading(false);
      }
    };
    fetchFeaturedCake();
  }, []);

  const addToCart = (cake) => {
    dispatch({ type: 'ADD_TO_CART', payload: cake });
  };

  const handleAddToCart = async (cake) => {
    if (isAddingToCart) return;
    setIsAddingToCart(true);
    addToCart(cake);
    setNotification(cake);
    setTimeout(() => {
      setNotification(null);
      setIsAddingToCart(false);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#F5EFE4] text-[#3D2817]">
      <Navigation onCartOpen={onCartOpen} />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="space-y-8 order-2 md:order-1">
            <div className="space-y-4">
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-tight tracking-tight">
                The perfect gift.
              </h1>
              <p className="font-serif italic text-3xl md:text-4xl text-[#6B7C3F]">
                Made simple.
              </p>
            </div>

            <Divider />

            <p className="uppercase tracking-[0.2em] text-sm text-[#6B5644] max-w-md">
              Fresh pound cake, beautifully packaged
            </p>

            <div className="grid grid-cols-3 gap-4 max-w-md pt-2">
              <div className="flex flex-col items-center text-center gap-2">
                <Gift className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
                <p className="text-[11px] tracking-[0.15em] uppercase text-[#3D2817] font-medium leading-tight">
                  Ready in<br />24 hours
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 border-x border-[#E5DCC9] px-2">
                <Truck className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
                <p className="text-[11px] tracking-[0.15em] uppercase text-[#3D2817] font-medium leading-tight">
                  Delivered<br />with care
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-2">
                <Heart className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
                <p className="text-[11px] tracking-[0.15em] uppercase text-[#3D2817] font-medium leading-tight">
                  Made to<br />make you smile
                </p>
              </div>
            </div>

            <div className="pt-2">
              <Link
                to="/cakes"
                className="inline-block bg-[#6B7C3F] text-[#FAF6EE] px-10 py-4 tracking-[0.2em] uppercase text-sm hover:bg-[#5A6A35] transition-colors"
              >
                Order Now
              </Link>
            </div>
          </div>

          <div className="order-1 md:order-2">
            {loading ? (
              <div className="aspect-square bg-[#EFE7D6] animate-pulse rounded-sm" />
            ) : featuredCake ? (
              <div className="relative">
                <div className="aspect-square overflow-hidden bg-[#EFE7D6]">
                  <img
                    src={featuredCake.image}
                    alt={featuredCake.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/400/320';
                      e.target.alt = 'Image not available';
                    }}
                  />
                </div>
                <div className="mt-6 text-center space-y-3">
                  <h3 className="font-serif text-2xl">{featuredCake.name}</h3>
                  <p className="text-[#6B5644]">${featuredCake.price}</p>
                  <button
                    onClick={() => handleAddToCart(featuredCake)}
                    disabled={isAddingToCart}
                    className="inline-flex items-center justify-center gap-2 border border-[#3D2817] text-[#3D2817] hover:bg-[#3D2817] hover:text-[#FAF6EE] transition-colors px-8 py-3 tracking-[0.15em] uppercase text-xs disabled:opacity-50"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {isAddingToCart ? 'Adding…' : 'Add to cart'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="aspect-square flex items-center justify-center text-[#6B5644]">
                No cakes available at the moment
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Story strip */}
      <section className="bg-[#FAF6EE] py-20 border-y border-[#E5DCC9]">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <h2 className="font-serif text-3xl md:text-4xl text-[#6B7C3F]">Beautifully delivered</h2>
          <Divider />
          <p className="text-[#3D2817] leading-relaxed text-lg max-w-2xl mx-auto">
            Started by our founder Monica in 2023 as a way to honor her mother Nena's legacy,
            using family recipes passed down for over 60 years to make the most delicious pound
            cakes in South Florida.
          </p>
        </div>
      </section>

      {/* Collection */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <CakeCollection
            addToCart={addToCart}
            showAboutCard={true}
            limit={8}
          />
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-[#FAF6EE] py-14 border-t border-[#E5DCC9]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center">
          <div className="flex flex-col items-center gap-3">
            <Gift className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
            <p className="font-serif text-sm tracking-[0.1em] uppercase">Perfect for any occasion</p>
            <p className="text-xs text-[#6B5644] leading-relaxed">Birthdays, thank yous, holidays & more</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <Sparkles className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
            <p className="font-serif text-sm tracking-[0.1em] uppercase">Premium ingredients</p>
            <p className="text-xs text-[#6B5644] leading-relaxed">Quality you can taste in every bite</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <Clock className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
            <p className="font-serif text-sm tracking-[0.1em] uppercase">Ready quickly</p>
            <p className="text-xs text-[#6B5644] leading-relaxed">Order today, enjoy soon after</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <MapPin className="w-7 h-7 text-[#6B7C3F]" strokeWidth={1.5} />
            <p className="font-serif text-sm tracking-[0.1em] uppercase">Local delivery</p>
            <p className="text-xs text-[#6B5644] leading-relaxed">Across Miami, delivered with care</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F5EFE4]">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <h2 className="font-serif text-3xl md:text-4xl text-[#3D2817]">
            Life is sweeter when you share.
          </h2>
          <Divider />
          <p className="font-serif italic text-xl text-[#6B7C3F]">Let us deliver the sweet.</p>
          <div className="pt-4">
            <Link
              to="/cakes"
              className="inline-block bg-[#6B7C3F] text-[#FAF6EE] px-10 py-4 tracking-[0.2em] uppercase text-sm hover:bg-[#5A6A35] transition-colors"
            >
              Order Today
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6B7C3F] text-[#FAF6EE]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm tracking-[0.1em]">
          <p>© 2024 Nena Miami. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <span>Miami, FL</span>
            <Link to="/contact" className="hover:opacity-80 transition-opacity uppercase">Contact</Link>
            <a
              href="https://instagram.com/nenamiami"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity uppercase"
            >
              Instagram
            </a>
          </div>
        </div>
      </footer>

      {/* Add to Cart Notification */}
      {notification && (
        <div className="fixed bottom-6 right-6 bg-[#3D2817] text-[#FAF6EE] px-5 py-4 shadow-lg z-50 flex items-center gap-3">
          <ShoppingCart className="w-5 h-5" />
          <p className="text-sm">{notification.name} added to cart</p>
        </div>
      )}
    </div>
  );
};

export default ModernNenaMiami;
